import { useInView } from 'framer-motion';
import {
  DetailedHTMLProps,
  FC,
  MouseEventHandler,
  ReactElement,
  SourceHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  VideoHTMLAttributes,
} from 'react';
import styled, { css } from 'styled-components';

type VideoSource = DetailedHTMLProps<SourceHTMLAttributes<HTMLSourceElement>, HTMLSourceElement>;

interface InlineVideoProps extends DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement> {
  children: ReactElement<VideoSource> | ReactElement<VideoSource>[];
  $lazy?: boolean;
}

/**
 * See: https://github.com/facebook/react/issues/6544
 *      https://web.dev/lazy-loading-video/
 */
const InlineMutedVideo: FC<InlineVideoProps> = ({ children: originalChildren, ...props }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.25 });
  const loadSources = useMemo(() => !props.$lazy || isInView, [props.$lazy, isInView]);

  const children = useMemo(() => {
    if (loadSources) {
      return originalChildren;
    }

    const modifySource = (source) => {
      return {
        ...source,
        props: {
          ...source.props,
          'src': undefined,
          'data-src': source.props.src,
        },
      };
    };

    if (Array.isArray(originalChildren)) {
      return originalChildren.map(modifySource);
    }

    return modifySource(originalChildren);
  }, [originalChildren, loadSources]);

  useEffect(() => {
    if (ref.current && props.muted === true) {
      ref.current.muted = props.muted;
      ref.current.defaultMuted = props.muted;
    }
  });

  useEffect(() => {
    const video = ref.current;

    if (video && loadSources) {
      video.load();

      if (props.autoPlay) {
        video.play().catch((e) => {
          // Commonly a NotAllowedError, which we can just silently ignore
        });
      }
    }
  }, [loadSources]);

  const onVideoClick: MouseEventHandler<HTMLVideoElement> = (e) => {
    const video = ref.current;

    if (video && isInView) {
      if (video.paused || video.ended || video.currentTime === 0) {
        video.play().catch((e) => {
          // Commonly a NotAllowedError, which we can just silently ignore
        });
      }
    }

    if (props.onClick) props.onClick(e);
  };

  return (
    <Video {...props} ref={ref} onClick={onVideoClick}>
      {children}
    </Video>
  );
};

InlineMutedVideo.defaultProps = {
  $lazy: true,
};

export default InlineMutedVideo;

const Video = styled.video`
  ${(props) =>
    props.width && props.height
      ? css`
          aspect-ratio: ${props.width} / ${props.height};
        `
      : null};
`;
